import { ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router';
import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { mergeQueryString, updateSearch } from 'bernie-core';
import { QueryObject, UpdateSearchProps } from 'bernie-core/dist/routing/update-search';

import {
  getPropertySearchCriteriaFromQueryParams,
  LodgingQueryParamsProvider,
  useLodgingURLParamsToQueryInputs,
} from '@shared-ui/search-tools-lodging';
import { LodgingSortAndFilters, SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';

import { evaluateAndLogExperiment } from 'src/common/stores/utils';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { packageTypeMap } from 'src/common/stores/adapters';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { Field, FieldChangeCallback } from 'uitk-react-form/dist/uitk-form-provider';

import { buildUrlQueryString, Categories } from './universal-sort-filter-utils';

interface UniversalSortFilterProps {
  context?: ContextStore;
  experiment?: ExperimentStore;
  multiItem?: MultiItemStore;
  page?: PageStore;
  searchResults?: SearchResultsStore;
}
interface UniversalSortFilterWrapperProps {
  className?: string;
  view: SortAndFiltersViewType;
  onOpenDialog?: () => void;
}

const UniversalSortFilter: React.FC<UniversalSortFilterProps & UniversalSortFilterWrapperProps> = inject(
  'context',
  'experiment',
  'multiItem',
  'page',
  'searchResults',
  'onOpenDialog'
)(
  observer(({ context, experiment, multiItem, page, searchResults, view, className = '', onOpenDialog }) => {
    const [urlQueryString, setUrlQueryString] = React.useState('');

    const history = useHistory();
    const location = useLocation();
    const isFiltersDialog = [
      SortAndFiltersViewType.SORT_AND_FILTERS,
      SortAndFiltersViewType.FILTER_DIALOG,
      SortAndFiltersViewType.FILTER_BAR,
    ].includes(view);
    const isSearchResultsLoading = searchResults?.isLoading;

    const updateSearchWithQuery = (query: UpdateSearchProps['newParams']) => {
      const queryCombiner = (_prevQuery: QueryObject, newQuery: QueryObject) => newQuery;
      updateSearch({ history, location, newParams: query, replace: false, queryCombiner });
    };

    const onFieldChange = (field: Field, event: React.ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = field;
      const existingUrlQueryString = isFiltersDialog && urlQueryString !== '' ? urlQueryString : history.location.search;
      const newQueryString = buildUrlQueryString({ name, value, existingUrlQueryString, event });
      // this component renders several different types of filter views - when the child filter view is a dialog
      // we simply capture filter field changes as local state in the form of a new query string
      // if it's not a dialog, we go ahead and update the page's query string with the new one we generated
      if (isFiltersDialog) {
        setUrlQueryString(newQueryString);
      } else {
        updateSearchWithQuery(newQueryString);
        window.scrollTo(0, 0);
      }
    };

    const onSubmitFilters = React.useCallback(() => {
      const currentQueryString = urlQueryString === '' ? history.location.search : urlQueryString;
      const newQueryString = mergeQueryString(currentQueryString, {}, key => !key.startsWith('pwaDialog'));

      setTimeout(() => {
        updateSearchWithQuery(newQueryString);
        window.scrollTo(0, 0);
      }, 50);
      setUrlQueryString('');
    }, [urlQueryString]);

    const inputs = useLodgingURLParamsToQueryInputs();
    const { primary } = inputs?.propertySearchCriteriaInput ?? {};

    const multiItemObj = multiItem?.multiItemContext?.id
      ? {
          id: multiItem.multiItemContext?.id,
          packageType: multiItem.multiItemContext?.packageType ? packageTypeMap[multiItem.multiItemContext?.packageType] : undefined,
        }
      : undefined;

    const categories: Categories = {
      destination: primary?.destination ?? {},
      criteria: inputs?.propertySearchCriteriaInput ?? {},
      shoppingContext: {
        multiItem: multiItemObj,
      },
    };

    const filterSheetFooterCategories: Categories = {
      ...categories,
      criteria: getPropertySearchCriteriaFromQueryParams(
        new URLSearchParams(urlQueryString === '' ? history.location.search : urlQueryString)
      ).propertySearchCriteriaInput,
    };

    const onFieldClick = (field: Field): void => {
      const { name } = field;

      if (name === 'sort' && context && experiment && page) {
        evaluateAndLogExperiment('Guest_Rating_sort_option_to_non_ML_Model', context, experiment, page);
      }
    };

    return (
      <div className={className}>
        <LodgingSortAndFilters
          onFieldChange={onFieldChange as FieldChangeCallback}
          onFieldClick={onFieldClick}
          onSubmit={onSubmitFilters}
          onOpenDialog={onOpenDialog}
          view={view}
          inputs={categories}
          loading={isSearchResultsLoading}
          filterSheetFooterInputs={filterSheetFooterCategories}
        />
      </div>
    );
  })
);

export const UniversalSortFilterWrapper = ({ view, className, onOpenDialog }: UniversalSortFilterWrapperProps) => {
  const location = useLocation();

  return (
    <ErrorBoundary label="hotel.search.universal-sort-filter">
      <LodgingQueryParamsProvider urlSearchParams={new URLSearchParams(location.search)}>
        <UniversalSortFilter view={view} className={className} onOpenDialog={onOpenDialog} />
      </LodgingQueryParamsProvider>
    </ErrorBoundary>
  );
};
